import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatMoney, isOpen, MEAL_LABELS } from '@mealdesk/shared';
import { PhotoSlot } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import {
  useMoney,
  useTimeLeft,
  useDeliveryPoints,
  useOffer,
  useOrderFor,
  usePlaceOrder,
  useProfile,
} from '../lib/ordering.js';

/**
 * The dish, then the decision.
 *
 * The design gives this screen a photograph running to the top edge with a
 * rounded sheet riding up over it, and pins the price and the one action to the
 * bottom. Screens 06 and 07 of the study are two steps — look at the food, then
 * choose a pickup point — but there is one order per meal and no cart, so they
 * collapse into a single screen with nothing lost.
 */
export default function OrderPage() {
  // Date and meal are in the path, not just the dish: the same item is served
  // on more than one day, and the id alone would not say which.
  const { serviceDate, mealType, menuItemId } = useParams();
  const navigate = useNavigate();
  const { offer, isLoading } = useOffer(serviceDate, mealType, menuItemId);
  const points = useDeliveryPoints();
  const profile = useProfile();
  const { t } = useT();
  // Default to showing it: a profile still loading should not flash a price
  // into existence a moment later, and the company's choice arrives with it.
  const showBilling = profile?.showBilling ?? true;
  // The kitchen caps some days. Finding that out from a refused order is
  // finding out after deciding.
  const full = offer ? offer.capacityRemaining !== null && offer.capacityRemaining < 1 : false;
  const money = useMoney()(offer?.price ?? 0, offer?.employeeShare ?? 0, offer?.currency ?? 'USD');
  const existing = useOrderFor(offer?.serviceDate ?? '', offer?.mealType ?? '');
  const left = useTimeLeft(offer?.cutoffAt);
  const place = usePlaceOrder();
  const [chosen, setChosen] = useState<string | null>(null);

  const pointId = chosen ?? profile?.defaultPointId ?? points[0]?.id ?? '';

  if (isLoading) {
    return <p className="p-6 text-[15px] md:text-[16.5px] text-muted">Loading…</p>;
  }

  if (!offer) {
    return <p className="p-6 text-[15px] md:text-[16.5px] text-muted">That menu is no longer available.</p>;
  }

  const open = isOpen(offer.cutoffAt);
  const switching = existing && existing.restaurantName !== offer.restaurantName;
  const dateLabel = new Date(`${offer.serviceDate}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const submit = () => {
    place.mutate(
      { offer, deliveryPointId: pointId },
      { onSuccess: (result) => navigate(`/confirm/${result.id}`, { replace: true }) },
    );
  };

  return (
    <div className="bg-white">
      <div className="relative">
        <PhotoSlot label={offer.title} src={offer.photoUrl} height={330} />
        <div className="absolute top-3.5 right-4 left-4 flex items-center">
          <Link
            to="/menu"
            aria-label="Back"
            className="flex size-10 items-center justify-center rounded-full bg-white text-[19px] md:text-[21px] font-extrabold"
          >
            ‹
          </Link>
        </div>
      </div>

      {/* The sheet rides up over the photograph rather than sitting below it. */}
      <div className="relative -mt-[30px] rounded-t-[34px] bg-white px-6 pt-[26px] pb-[180px]">
        <span className="text-[13px] md:text-[14.5px] font-bold text-brand">
          {offer.restaurantName} · {dateLabel} {t(MEAL_LABELS[offer.mealType]).toLowerCase()}
        </span>

        <h2 className="mt-1.5 mb-2.5 text-[26px] font-extrabold text-pretty">{offer.title}</h2>

        {offer.description && (
          <p className="mb-5 text-[14.5px] md:text-[15.5px] leading-[1.6] text-muted">{offer.description}</p>
        )}

        {offer.allergens.length > 0 && (
          <>
            <b className="mb-2.5 block text-[13px] md:text-[14.5px] font-extrabold tracking-[0.07em] text-subtle uppercase">
              Allergens
            </b>
            <div className="mb-[22px] flex flex-wrap gap-2">
              {offer.allergens.map((allergen) => (
                <span
                  key={allergen}
                  className="rounded-pill bg-warn-soft px-3.5 py-2 text-[13px] md:text-[14.5px] font-bold text-warn-deep capitalize"
                >
                  {allergen}
                </span>
              ))}
            </div>
          </>
        )}

        <b className="mb-2.5 block text-[13px] md:text-[14.5px] font-extrabold tracking-[0.07em] text-subtle uppercase">
          Pickup point
        </b>
        <fieldset className="mb-6 flex flex-col gap-2.5">
          <legend className="sr-only">Pickup point</legend>

          {points.length === 0 && (
            <p className="rounded-control bg-panel px-4 py-4 text-[14px] md:text-[15px] text-muted">
              No pickup points are set up for your site yet. Ask your company admin.
            </p>
          )}

          {points.map((point) => {
            const selected = point.id === pointId;
            return (
              <label
                key={point.id}
                className={`flex cursor-pointer items-center gap-3.5 rounded-control px-4 py-4 ${
                  selected
                    ? 'border-[1.5px] border-brand-bright bg-panel'
                    : 'border-[1.5px] border-transparent bg-panel'
                }`}
              >
                <input
                  type="radio"
                  name="deliveryPoint"
                  className="sr-only"
                  checked={selected}
                  onChange={() => setChosen(point.id)}
                />
                <span
                  aria-hidden
                  className={`flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 ${
                    selected ? 'border-brand-bright' : 'border-line-dashed'
                  }`}
                >
                  <span
                    className={`size-[11px] rounded-full ${selected ? 'bg-brand-bright' : 'bg-transparent'}`}
                  />
                </span>
                <span className="min-w-0 leading-[1.3]">
                  <b className="block text-[15px] md:text-[16.5px]">{point.name}</b>
                  <span className="text-[13px] md:text-[14.5px] text-muted">{point.note}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

{/*
         * What the meal costs and who pays is the organisation's to disclose,
         * and some treat it as payroll's business. What is never hidden is an
         * employee's own share: charging somebody an amount they are not
         * allowed to see is not a display preference.
         */}
        {(showBilling || offer.employeeShare > 0) && (
          <>
            <b className="mb-2.5 block text-[13px] font-extrabold tracking-[0.07em] text-subtle uppercase md:text-[14.5px]">
              {t('Billing')}
            </b>
            <div className="rounded-tile bg-panel px-[18px] py-[17px] text-[13.5px] md:text-[15px]">
              {showBilling && (
                <div className="mb-2 flex justify-between">
                  <span className="text-muted">{t(MEAL_LABELS[offer.mealType])} × 1</span>
                  <b>{formatMoney(offer.price, offer.currency)}</b>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted">{t('Your share')}</span>
                <b>{formatMoney(offer.employeeShare, offer.currency)}</b>
              </div>
              {showBilling && (
                <div className="mt-2 flex justify-between border-t border-line-strong pt-2.5">
                  <b>Charged to {profile?.departmentName ?? 'your company'}</b>
                  <b>{formatMoney(offer.price - offer.employeeShare, offer.currency)}</b>
                </div>
              )}
            </div>
          </>
        )}

        {switching && (
          <p className="mt-3.5 rounded-tile bg-warn-soft px-4 py-3.5 text-[13.5px] md:text-[15px] leading-[1.5] text-warn-deep">
            You already ordered {existing?.restaurantName} for this meal. Placing this order
            replaces it.
          </p>
        )}
      </div>

      {/* Price and the single action, pinned where a thumb reaches. */}
      <div className="fixed inset-x-0 bottom-[var(--tabbar)] z-10 mx-auto max-w-[430px] rounded-t-[28px] bg-white px-6 pt-4 pb-5 shadow-[0_-8px_28px_rgba(38,64,28,.10)]">
        <div className="mb-[11px] flex items-center">
          <span className="text-[13px] md:text-[14.5px] font-bold text-warn-ink">
            {full
              ? t('The kitchen has taken all it can cook for this day.')
              : left
                ? `${left.long} ${t('left · closes {when}', { when: left.when })}`
                : t('This meal is closed')}
          </span>
          {/* The bar carries whatever the person is allowed to know they owe. */}
          {money && <b className="ml-auto text-[17px] md:text-[18.5px]">{money}</b>}
        </div>

        {place.isError && (
          <p className="mb-2.5 rounded-chip bg-warn-soft px-3.5 py-2.5 text-[13.5px] md:text-[15px] text-warn-deep">
            {place.error.message}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!open || full || place.isPending}
          className="flex h-[56px] w-full items-center justify-center rounded-control bg-brand text-[16.5px] md:text-[18.5px] font-extrabold text-white disabled:opacity-45"
        >
          {full
            ? t('Full')
            : place.isPending
              ? `${t('Place order')}…`
              : switching
                ? t('Replace order')
                : t('Place order')}
        </button>
      </div>
    </div>
  );
}
